import { Marked, Renderer } from 'marked'

const escape = value => String(value).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char])

export function safeWebUrl(href) {
  if (!/^https?:\/\//i.test(href) || /[\s\u0000-\u0020\\]/u.test(href)) return false
  try { return /^https?:$/.test(new URL(href).protocol) } catch { return false }
}

export function localImageKey(href) {
  if (!/^\.\.\/assets\/[^\s?#\\\u0000-\u0020]+\.(?:png|jpe?g|webp|gif|svg)$/u.test(href) || href.slice(3).split('/').includes('..')) return null
  return href
}

// Relative Markdown links and local images are resolved against the maintained
// catalog/asset map, never accepted as arbitrary filesystem or application URLs.
export function renderGuide(markdown, { resolveLink, resolveImage }) {
  const headings = []
  const ids = new Set()
  const renderer = new Renderer()
  const table = renderer.table
  renderer.html = ({ text }) => escape(text)
  renderer.link = function ({ href, title, tokens }) {
    const label = this.parser.parseInline(tokens)
    const target = resolveLink(href)
    if (!target || (!target.startsWith('#') && !/^\/(?!\/)/.test(target) && !safeWebUrl(target)) || /[\u0000-\u0020\\]/.test(target)) return label
    return `<a href="${escape(target)}"${title ? ` title="${escape(title)}"` : ''}${safeWebUrl(target) ? ' target="_blank" rel="noopener noreferrer"' : ''}>${label}</a>`
  }
  renderer.image = ({ href, text, title }) => {
    const target = resolveImage(href)
    if (!target || /[\u0000-\u0020\\]/.test(target) || (!/^\/(?!\/)/.test(target) && !safeWebUrl(target) && !/^data:image\/(?:png|jpeg|webp|gif|svg\+xml);base64,[a-z\d+/=]+$/i.test(target))) return escape(text)
    return `<img src="${escape(target)}" alt="${escape(text)}"${title ? ` title="${escape(title)}"` : ''} loading="lazy" decoding="async">`
  }
  renderer.heading = function ({ depth, tokens }) {
    // Parse visible text, avoiding Markdown syntax in the table of contents.
    const html = this.parser.parseInline(tokens)
    const title = html.replace(/<[^>]*>/g, '').replace(/&(?:amp|lt|gt|quot|#39);/g, entity => ({ '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" })[entity])
    const base = title.toLowerCase().replace(/[^\p{L}\p{N}\s-]/gu, '').trim().replace(/\s+/g, '-') || 'section'
    let id = base
    let count = 1
    while (ids.has(id)) id = `${base}-${count++}`
    ids.add(id)
    if (depth === 2 || depth === 3) headings.push({ id, title, depth })
    return `<h${depth} id="${escape(id)}">${html}</h${depth}>\n`
  }
  renderer.table = function (token) { return `<div class="guide-table" tabindex="0" role="region" aria-label="Table">${table.call(this, token)}</div>` }
  return { html: new Marked({ gfm: true, async: false, renderer }).parse(markdown), headings }
}
